import type { LayerSpecification, Map as MapLibreMap, StyleSpecification } from 'maplibre-gl';

import { apiOrigin } from '../api/origin';

/**
 * Basemap cartography for the tiles Planetiler produces.
 *
 * Planetiler's default profile emits the OpenMapTiles schema, so the
 * Protomaps style packages do not apply. Rather than pull in a mismatched
 * dependency or a 3,000-line stock style, this styles the layers a route
 * planner actually needs, in both light and dark.
 *
 * Everything is applied IMPERATIVELY after the map has loaded. Handing
 * react-map-gl a new `mapStyle` object re-issues `setStyle`, and repeated
 * calls leave MapLibre rebuilding the style forever — a permanently blank
 * map. The map is created once with INITIAL_STYLE and never restyled.
 */

export interface Palette {
  land: string;
  landuse: string;
  residential: string;
  wood: string;
  grass: string;
  park: string;
  water: string;
  waterway: string;
  building: string;
  motorway: string;
  motorwayCasing: string;
  primary: string;
  primaryCasing: string;
  minor: string;
  minorCasing: string;
  rail: string;
  boundary: string;
  label: string;
  labelHalo: string;
  labelWater: string;
}

export const LIGHT: Palette = {
  land: '#f7f6f2',
  landuse: '#f0eee7',
  residential: '#efece5',
  wood: '#d7e3c8',
  grass: '#e2ecd6',
  park: '#d8e8c8',
  water: '#aacce0',
  waterway: '#9dc4da',
  building: '#e5e0d5',
  motorway: '#fbc98a',
  motorwayCasing: '#e8a95c',
  primary: '#fddfae',
  primaryCasing: '#ecc487',
  minor: '#ffffff',
  minorCasing: '#e6e1d5',
  rail: '#d3cec2',
  boundary: '#b9b2a4',
  label: '#5a564d',
  labelHalo: '#ffffff',
  labelWater: '#5b87a3',
};

export const DARK: Palette = {
  land: '#11171a',
  landuse: '#161d20',
  residential: '#171e21',
  wood: '#16261c',
  grass: '#18291d',
  park: '#192c1e',
  water: '#123243',
  waterway: '#17475c',
  building: '#1f272b',
  motorway: '#4d4432',
  motorwayCasing: '#2c2820',
  primary: '#3d3a2d',
  primaryCasing: '#262420',
  minor: '#2a3134',
  minorCasing: '#171d20',
  rail: '#2e3437',
  boundary: '#3d4548',
  label: '#aab3b0',
  labelHalo: '#0d1214',
  labelWater: '#6f9bb5',
};

export const paletteFor = (dark: boolean): Palette => (dark ? DARK : LIGHT);

export const BACKGROUND_LAYER_ID = 'background';
export const BASEMAP_SOURCE = 'omt';

/**
 * The one and only style handed to the map. Its identity never changes, so
 * react-map-gl never calls setStyle. Colours are updated in place afterwards.
 *
 * Glyphs are served by our own backend from PBF ranges in data/fonts, so
 * labels work without any external font server. `/fonts` is a top-level
 * backend route, not one the vite dev proxy forwards (that proxy only
 * forwards `/api`), so this must be the backend's absolute origin rather
 * than a relative path.
 */
const API_ORIGIN = apiOrigin;

export const INITIAL_STYLE: StyleSpecification = {
  version: 8,
  // Glyphs come from our own backend, so labels add no external dependency.
  glyphs: `${API_ORIGIN}/fonts/{fontstack}/{range}.pbf`,
  sources: {},
  layers: [
    {
      id: BACKGROUND_LAYER_ID,
      type: 'background',
      paint: { 'background-color': LIGHT.land },
    },
  ],
};

/** Repaints every basemap layer without rebuilding the style. */
export function applyTheme(map: MapLibreMap, dark: boolean): void {
  const palette = paletteFor(dark);
  if (map.getLayer(BACKGROUND_LAYER_ID)) {
    map.setPaintProperty(BACKGROUND_LAYER_ID, 'background-color', palette.land);
  }
  for (const layer of basemapLayers(dark)) {
    if (!map.getLayer(layer.id)) continue;
    for (const [property, value] of Object.entries(layer.paint ?? {})) {
      // Paint keys are typed per layer type; this loop is generic by design.
      map.setPaintProperty(layer.id, property as never, value as never);
    }
  }
}

/**
 * Basemap layers, bottom to top.
 *
 * Draw order is array order: ground cover, then water, then buildings, then
 * roads (casing beneath fill so junctions read as continuous), then
 * boundaries. Route and avoidance layers sit above all of this.
 */
function basemapLayers(dark: boolean): LayerSpecification[] {
  const c = paletteFor(dark);

  return [
    // ── ground cover ────────────────────────────────────────────────
    {
      id: 'omt-landuse',
      type: 'fill',
      source: BASEMAP_SOURCE,
      'source-layer': 'landuse',
      paint: {
        'fill-color': [
          'match', ['get', 'class'],
          'residential', c.residential,
          'cemetery', c.grass,
          c.landuse,
        ],
        'fill-opacity': 0.9,
      },
    },
    {
      id: 'omt-landcover',
      type: 'fill',
      source: BASEMAP_SOURCE,
      'source-layer': 'landcover',
      paint: {
        'fill-color': [
          'match', ['get', 'class'],
          'wood', c.wood,
          'forest', c.wood,
          'grass', c.grass,
          'farmland', c.grass,
          c.grass,
        ],
        'fill-opacity': 0.85,
      },
    },
    {
      id: 'omt-park',
      type: 'fill',
      source: BASEMAP_SOURCE,
      'source-layer': 'park',
      paint: { 'fill-color': c.park, 'fill-opacity': 0.75 },
    },

    // ── water ───────────────────────────────────────────────────────
    {
      id: 'omt-water',
      type: 'fill',
      source: BASEMAP_SOURCE,
      'source-layer': 'water',
      paint: { 'fill-color': c.water },
    },
    {
      id: 'omt-waterway',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'waterway',
      minzoom: 9,
      paint: {
        'line-color': c.waterway,
        'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.6, 16, 4],
      },
    },

    // ── buildings ───────────────────────────────────────────────────
    {
      id: 'omt-building',
      type: 'fill',
      source: BASEMAP_SOURCE,
      'source-layer': 'building',
      minzoom: 13,
      paint: {
        'fill-color': c.building,
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 13, 0, 15, 0.85],
      },
    },

    // ── rail ────────────────────────────────────────────────────────
    {
      id: 'omt-rail',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 11,
      filter: ['==', ['get', 'class'], 'rail'],
      paint: {
        'line-color': c.rail,
        'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 16, 2],
        'line-dasharray': [3, 2],
      },
    },

    // ── road casings, widest class first ────────────────────────────
    {
      id: 'omt-road-minor-casing',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 13,
      filter: ['in', ['get', 'class'], ['literal', ['minor', 'service']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': c.minorCasing,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 1.4, 18, 8],
      },
    },
    {
      id: 'omt-road-primary-casing',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 6,
      filter: ['in', ['get', 'class'], ['literal', ['primary', 'secondary', 'tertiary', 'trunk']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': c.primaryCasing,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.2, 9, 1.6, 16, 12],
      },
    },
    {
      id: 'omt-road-motorway-casing',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 4,
      filter: ['==', ['get', 'class'], 'motorway'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': c.motorwayCasing,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1.4, 6, 2.2, 16, 15],
      },
    },

    // ── road fills ──────────────────────────────────────────────────
    {
      id: 'omt-road-path',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 14,
      filter: ['in', ['get', 'class'], ['literal', ['path', 'track']]],
      paint: {
        'line-color': c.minorCasing,
        'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.6, 18, 2],
        'line-dasharray': [2, 2],
      },
    },
    {
      id: 'omt-road-minor',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 13,
      filter: ['in', ['get', 'class'], ['literal', ['minor', 'service']]],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': c.minor,
        'line-width': ['interpolate', ['linear'], ['zoom'], 13, 0.7, 18, 6],
      },
    },
    {
      id: 'omt-road-primary',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 6,
      /*
       * Trunk roads from zoom 6, everything else from 9: at a regional scale
       * the trunk network is what a place looks like, while every tertiary
       * road at that zoom is a grey smear over it.
       */
      filter: [
        'any',
        ['==', ['get', 'class'], 'trunk'],
        ['all',
          ['in', ['get', 'class'], ['literal', ['primary', 'secondary', 'tertiary']]],
          ['>=', ['zoom'], 9]],
      ],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': c.primary,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.7, 9, 0.9, 16, 9],
      },
    },
    {
      id: 'omt-road-motorway',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation',
      minzoom: 4,
      filter: ['==', ['get', 'class'], 'motorway'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': c.motorway,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.8, 6, 1.4, 16, 11],
      },
    },

    // ── labels, above all geometry ──────────────────────────────────
    {
      id: 'omt-label-water',
      type: 'symbol',
      source: BASEMAP_SOURCE,
      'source-layer': 'water_name',
      minzoom: 6,
      layout: {
        'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
        'text-font': ['Noto Sans Italic'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 10, 10, 16, 14],
        'text-max-width': 7,
      },
      paint: { 'text-color': c.labelWater, 'text-halo-color': c.labelHalo, 'text-halo-width': 1.2 },
    },
    {
      id: 'omt-label-park',
      type: 'symbol',
      source: BASEMAP_SOURCE,
      'source-layer': 'park',
      minzoom: 12,
      layout: {
        'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 12, 9, 16, 12],
        'text-max-width': 8,
      },
      paint: { 'text-color': c.label, 'text-halo-color': c.labelHalo, 'text-halo-width': 1.2 },
    },
    {
      id: 'omt-label-road',
      type: 'symbol',
      source: BASEMAP_SOURCE,
      'source-layer': 'transportation_name',
      minzoom: 13,
      layout: {
        'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 9, 18, 13],
        'symbol-placement': 'line',
        'text-rotation-alignment': 'map',
        'text-padding': 3,
      },
      paint: { 'text-color': c.label, 'text-halo-color': c.labelHalo, 'text-halo-width': 1.4 },
    },
    {
      id: 'omt-label-place',
      type: 'symbol',
      source: BASEMAP_SOURCE,
      'source-layer': 'place',
      minzoom: 3,
      /*
       * Widened as you zoom in, rather than all classes from the start.
       *
       * A state fitted to the window sits around zoom 7, and this layer used
       * to start at 8 — so the one view where names matter most had none at
       * all, and the map read as broken. Showing every village at that scale
       * would be the opposite mistake, crowding out the cities somebody is
       * actually navigating by, so each class earns its place by zoom.
       */
      filter: [
        'any',
        ['==', ['get', 'class'], 'city'],
        ['all', ['==', ['get', 'class'], 'town'], ['>=', ['zoom'], 7]],
        ['all', ['==', ['get', 'class'], 'village'], ['>=', ['zoom'], 10]],
        // 11, not 12: a city fitted to the window lands at about twelve, so a
        // threshold of twelve meant the first view of a city — the one where
        // somebody is deciding where to start — showed its name and nothing
        // else inside it.
        ['all', ['in', ['get', 'class'], ['literal', ['suburb', 'neighbourhood']]], ['>=', ['zoom'], 11]],
      ],
      layout: {
        'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
        'text-font': ['Noto Sans Medium'],
        'text-size': [
          'interpolate', ['linear'], ['zoom'],
          3, ['match', ['get', 'class'], 'city', 11, 'town', 10, 9],
          8, ['match', ['get', 'class'], 'city', 14, 'town', 11, 9],
          15, ['match', ['get', 'class'], 'city', 22, 'town', 16, 13],
        ],
        'text-max-width': 8,
      },
      paint: { 'text-color': c.label, 'text-halo-color': c.labelHalo, 'text-halo-width': 1.6 },
    },

    // ── administrative boundaries ───────────────────────────────────
    {
      id: 'omt-boundary',
      type: 'line',
      source: BASEMAP_SOURCE,
      'source-layer': 'boundary',
      minzoom: 4,
      filter: ['<=', ['get', 'admin_level'], 6],
      paint: {
        'line-color': c.boundary,
        'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 12, 1.6],
        'line-dasharray': [4, 3],
        'line-opacity': 0.7,
      },
    },
  ];
}

/** Ids of every vector basemap layer, for showing/hiding as a group. */
export function basemapLayerIds(): string[] {
  return basemapLayers(false).map((l) => l.id);
}

/**
 * Adds the vector basemap once a city's PMTiles archive exists. Inserted
 * before `beforeId` so the route always draws on top of the map.
 */
export function applyBasemap(
  map: MapLibreMap,
  pmtilesUrl: string,
  dark: boolean,
  beforeId?: string,
): void {
  const wanted = `pmtiles://${pmtilesUrl}`;

  /*
   * Replace the source when the archive changes, rather than only adding one.
   *
   * Guarding solely on "does a source exist" baked the first city's basemap in
   * for the life of the map: switching city reloaded its places and refitted
   * the view, but went on requesting the previous city's tiles, so the new one
   * drew blank. It also made a broken archive permanent — a city whose
   * basemap was mid-rebuild threw "Wrong magic number for PMTiles archive",
   * and no amount of switching away recovered it.
   *
   * A source cannot be removed while layers reference it, so the layers go
   * first and are rebuilt below.
   */
  const existing = map.getStyle()?.sources?.[BASEMAP_SOURCE] as { url?: string } | undefined;

  if (existing && existing.url !== wanted) {
    for (const id of basemapLayerIds()) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
    map.removeSource(BASEMAP_SOURCE);
  }

  if (!map.getSource(BASEMAP_SOURCE)) {
    map.addSource(BASEMAP_SOURCE, { type: 'vector', url: wanted });
  }
  for (const layer of basemapLayers(dark)) {
    if (map.getLayer(layer.id)) continue;
    map.addLayer(layer, beforeId && map.getLayer(beforeId) ? beforeId : undefined);
  }
}

